import confetti from "canvas-confetti";
import { AssignmentStatusKey, PersonAssignmentRetrieveType } from "../../api";
import { BoardColumnType, BoardConfigType } from "./board-layout-config";

export const makeCelebration = (theme: {
	chat: { sent: string };
	mermaidGraph: { rectBorderColor: string };
}) => {
	const end = Date.now() + 15 * 1000;
	const colors = [theme.chat.sent, theme.mermaidGraph.rectBorderColor];
	(function frame() {
		confetti({
			particleCount: 2,
			angle: 60,
			spread: 55,
			origin: { x: 0 },
			colors: colors,
		});
		confetti({
			particleCount: 2,
			angle: 120,
			spread: 55,
			origin: { x: 1 },
			colors: colors,
		});

		if (Date.now() < end) {
			requestAnimationFrame(frame);
		}
	})();
};

type ValidationResponse = {
	success: boolean;
	message: string;
};

export const validateAdd = (
	column: BoardColumnType,
	boardLayout: BoardConfigType
): ValidationResponse => {
	const boardLayoutCopy: BoardConfigType = JSON.parse(JSON.stringify(boardLayout));
	const allStatuses = boardLayoutCopy.columns
		.map((col) => {
			return col.statuses.map((status) => status.name);
		})
		.flat();
	if (boardLayoutCopy.columns.find((col) => col.name === column.name) !== undefined) {
		return {
			success: false,
			message: "Column with name already exists for this assignment",
		};
	}

	if (column.statuses.some((status) => allStatuses.includes(status.name))) {
		return { success: false, message: "Another column already has this status" };
	}

	if (boardLayout.columns.length > 9) {
		return { success: false, message: "You cannot add more than 9 columns" };
	}
	return { success: true, message: "Success" };
};

export const validateEdit = (
	personAssignments: PersonAssignmentRetrieveType[],
	column: BoardColumnType,
	initColumn: BoardColumnType,
	boardLayout: BoardConfigType,
	idx: number
): ValidationResponse => {
	const boardLayoutCopy: BoardConfigType = JSON.parse(JSON.stringify(boardLayout));
	let personAssignmentStatuses = [];
	if (personAssignments) {
		personAssignmentStatuses = personAssignments.map((pa) => pa.status_str);
	}
	const statusesInColumn = column.statuses.map((status) => status.name);
	const deletedStatuses = initColumn.statuses.filter(
		(status) => !statusesInColumn.includes(status.name)
	);

	const allStatuses = boardLayoutCopy.columns
		.map((col, currIdx) => {
			if (currIdx !== idx) {
				return col.statuses.map((status) => status.name.toLowerCase());
			}
		})
		.flat();

	if (
		boardLayoutCopy.columns.find((col, currIdx) => {
			return (
				col.name.trim().toLowerCase() === column.name.trim().toLowerCase() &&
				currIdx !== idx
			);
		}) !== undefined
	) {
		return {
			success: false,
			message: "Column with name already exists for this assignment",
		};
	}

	if (
		column.statuses.some((status) =>
			allStatuses.includes(status.name.toLowerCase())
		)
	) {
		return { success: false, message: "Another column already has this status" };
	}

	if (
		deletedStatuses.some((status) => personAssignmentStatuses.includes(status.name))
	) {
		return {
			success: false,
			message: "Cannot delete a status that is in use by a person assignment",
		};
	}

	return { success: true, message: "Success" };
};

export enum ViewParamKeys {
	ASSIGNMENT_ID = "assignmentId",
	PERSON_ID = "personId",
	PERSON_ASSIGNMENT_ID = "personAssignmentId",
	STATUS_FILTER = "statusFilter",
}

/** The board for one role; with a person, it opens on their card. */
export const boardHref = (
	assignmentId: number,
	person?: { personId: number; personAssignmentId: number }
): string => {
	const params = new URLSearchParams({
		[ViewParamKeys.ASSIGNMENT_ID]: String(assignmentId),
	});
	if (person) {
		params.set(ViewParamKeys.PERSON_ID, String(person.personId));
		params.set(
			ViewParamKeys.PERSON_ASSIGNMENT_ID,
			String(person.personAssignmentId)
		);
	}
	return `/board/v2?${params}`;
};

const DEFAULT_BOARD_STATUS_KEYS: AssignmentStatusKey[] = ["ACTIVE", "PREPARATION"];

export const DEFAULT_STATUS_FILTER = DEFAULT_BOARD_STATUS_KEYS.join(",");
